import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Dialog,
  Flex,
  HStack,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as WithdrawIcon } from 'assets/icons/withdraw.svg';
import { useTimer, useWithdrawSendOtp } from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import {
  Button,
  DIALOG_ACTION_BUTTON_PROPS,
  Input,
  SERVICE_FLOW_BORDER,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';
import { fixDecimal, yup } from 'utils';

type ApproveCryptoModalType = {
  onClose: () => void;
  isOpen: boolean;
  loading: boolean;
  data: {
    id: string;
    symbol: string;
    fee: number;
    amount: number;
    destination_amount: number;
  };
  // eslint-disable-next-line no-unused-vars
  onButton: (code: number) => void | Promise<void>;
};

const ApproveCryptoModal: React.FC<ApproveCryptoModalType> = ({
  onClose,
  isOpen,
  data,
  loading,
  onButton,
}: ApproveCryptoModalType) => {
  const { t } = useTranslation('component.withdraw.modal');
  const { counter, setCounter } = useTimer(0);

  const schema = yup.object().shape({
    code: yup.number().label(t('form.code')).required(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const onSubmit = async (data: { code: number }) => {
    try {
      await Promise.resolve(onButton(data.code));
      reset();
    } catch {
      /* API error toast; keep modal open for retry */
    }
  };

  const formProps = useInputAttributes(register, errors, {
    code: t('form.code'),
  });

  const { mutateAsync: requestToGetOtp, isPending: otpRequestLoading } =
    useWithdrawSendOtp();

  React.useEffect(() => {
    if (isOpen) {
      setCounter(120);
    }
  }, [isOpen]);

  return (
    <Box display="flex" alignItems="center">
      <Dialog.Root
        size="md"
        open={isOpen}
        placement="center"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            reset();
            onClose();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps()}>
              <ServiceDialogHeader>{t('confirm')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <ServiceDialogBody>
                <VStack align="center" justify="flex-start" h="100%" w="100%">
                  <VStack w="100%" align="center" justify="center" flex="auto">
                    <WithdrawIcon />
                    <Text fontSize="4xl" fontWeight="500" color="body">
                      {`${data.destination_amount} ${data.symbol}`}
                    </Text>
                  </VStack>
                  <Box
                    w="100%"
                    mt={2}
                    rounded="md"
                    borderWidth="1px"
                    borderColor={SERVICE_FLOW_BORDER}
                    bg="dark"
                    px={{ base: 3, md: 4 }}
                    py={1}
                  >
                    <VStack align="stretch" gap={0} w="100%">
                      <Flex
                        justify="space-between"
                        align="center"
                        gap={4}
                        py={3}
                        borderBottomWidth="1px"
                        borderBottomColor={SERVICE_FLOW_BORDER}
                      >
                        <Text fontSize="sm" color="body2" flexShrink={0}>
                          {t('amount')}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="body"
                          textAlign="right"
                          flex="1"
                          minW={0}
                        >
                          {`${data.amount} ${data.symbol}`}
                        </Text>
                      </Flex>
                      <Flex
                        justify="space-between"
                        align="center"
                        gap={4}
                        py={3}
                        borderBottomWidth="1px"
                        borderBottomColor={SERVICE_FLOW_BORDER}
                      >
                        <Text fontSize="sm" color="body2" flexShrink={0}>
                          {t('fee')}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="body"
                          textAlign="right"
                          flex="1"
                          minW={0}
                        >
                          {fixDecimal({
                            value: data.fee,
                            fractionDigits: 5,
                          })}
                        </Text>
                      </Flex>
                      <Flex
                        justify="space-between"
                        align="center"
                        gap={4}
                        py={3}
                      >
                        <Text fontSize="sm" color="body2" flexShrink={0}>
                          {t('destination_amount')}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="body"
                          textAlign="right"
                          flex="1"
                          minW={0}
                        >
                          {`${data.destination_amount}  ${data.symbol}`}
                        </Text>
                      </Flex>
                    </VStack>
                  </Box>
                  <Box
                    w="100%"
                    rounded="md"
                    borderWidth="1px"
                    borderColor={SERVICE_FLOW_BORDER}
                    bg="dark"
                    px={3}
                    py={2}
                  >
                    <Text
                      w="100%"
                      textAlign="center"
                      fontSize="sm"
                      color="info"
                    >
                      {t('codeSent')}
                    </Text>
                  </Box>
                  <Box w="100%">
                    <form
                      id="withdraw-crypto-approve-form"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Box
                        w="100%"
                        display="flex"
                        flexDir={{ base: 'column', lg: 'row' }}
                        gap={4}
                      >
                        <Input
                          {...formProps.code}
                          textAlign="center"
                          placeholder="      -      -      -      -      -      -      "
                          type="number"
                          min="0"
                          maxLength="6"
                          minLength="6"
                        />
                        <Button
                          variant="filled"
                          display="block"
                          title={
                            counter > 0 ? '( ' + counter + ' )' : t('sendCode')
                          }
                          isDisabled={counter > 0}
                          width={{ base: '100%', lg: '200px' }}
                          isLoading={otpRequestLoading}
                          onClick={() =>
                            requestToGetOtp([]).then(() => {
                              setCounter(120);
                            })
                          }
                          mt={{ base: 0, lg: 8 }}
                          {...DIALOG_ACTION_BUTTON_PROPS}
                        />
                      </Box>
                    </form>
                  </Box>
                </VStack>
              </ServiceDialogBody>
              <ServiceDialogFooter>
                <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                  <Button
                    variant="ghost"
                    title={t('cancel')}
                    onClick={onClose}
                    {...DIALOG_ACTION_BUTTON_PROPS}
                  />
                  <Button
                    form="withdraw-crypto-approve-form"
                    type="submit"
                    isLoading={loading}
                    variant="filled"
                    title={t('confirm')}
                    {...DIALOG_ACTION_BUTTON_PROPS}
                  />
                </HStack>
              </ServiceDialogFooter>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default ApproveCryptoModal;
